import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import type { Billing } from '@tallypg/contracts';
import { api, formatDate, formatMoney, useApi } from '../api';

type Method = 'card' | 'us_bank_account' | 'bank_transfer';

interface Quote {
  bonusMicroUsd: number;
  bonusPct: number;
  reasons: string[];
  creditedMicroUsd: number;
}

export function BillingPage() {
  const [params] = useSearchParams();
  const billing = useApi<Billing>('/billing');
  const [amount, setAmount] = useState(25);
  const [method, setMethod] = useState<Method>('card');
  const [save, setSave] = useState(true);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [ar, setAr] = useState<{ threshold: number; amount: number; methodId: string } | null>(null);
  const b = billing.data;
  const min = b ? b.minimumTopupMicroUsd / 1_000_000 : 10;

  useEffect(() => {
    const micro = Math.round(amount * 1_000_000);
    if (!Number.isFinite(micro) || micro <= 0) return;
    const t = setTimeout(() => {
      api<Quote>(`/billing/topup/quote?amountMicroUsd=${micro}&paymentMethod=${method}`)
        .then(setQuote)
        .catch(() => setQuote(null));
    }, 150);
    return () => clearTimeout(t);
  }, [amount, method]);

  const arState = ar ?? {
    threshold: (b?.autoRecharge.thresholdMicroUsd ?? 2_000_000) / 1_000_000,
    amount: (b?.autoRecharge.amountMicroUsd ?? 10_000_000) / 1_000_000,
    methodId: b?.autoRecharge.paymentMethodId ?? b?.savedPaymentMethods[0]?.id ?? '',
  };

  return (
    <div>
      <h1>Billing</h1>
      {params.get('topup') === 'success' && (
        <div className="banner">Thank you. Your credit will appear within a moment.</div>
      )}
      {params.get('topup') === 'pending' && (
        <div className="banner">
          Payment started. Bank debits take 2 to 5 business days to clear and transfers arrive when your bank
          sends them; the credit and your bonus appear as soon as the money does.
        </div>
      )}
      {b?.state === 'payment_required' && (
        <div className="banner danger">
          <strong>Payment required.</strong> Add credit to resume your databases. Nothing has been deleted.
        </div>
      )}
      <div className="grid cols-3">
        <div className="card">
          <div className="stat">Available balance</div>
          <div className="stat-value">{b ? formatMoney(b.balanceMicroUsd) : '…'}</div>
        </div>
        <div className="card">
          <div className="stat">Paid credit</div>
          <div className="stat-value">{b ? formatMoney(b.paidBalanceMicroUsd) : '…'}</div>
          <div className="muted">never expires</div>
        </div>
        <div className="card">
          <div className="stat">Trial credit</div>
          <div className="stat-value">{b ? formatMoney(b.promotionalBalanceMicroUsd) : '…'}</div>
          <div className="muted">
            {b?.trialExpiresAt
              ? `expires ${new Date(b.trialExpiresAt).toLocaleDateString()}`
              : 'expired or used'}
          </div>
        </div>
      </div>

      <div className="grid cols-2">
        <div className="card">
          <h2>Add credit</h2>
          <p className="muted">
            Prepaid credit is used as you go and never expires. Minimum {formatMoney(min * 1_000_000)}.
          </p>
          <div className="field">
            <label>Amount (USD)</label>
            <input
              type="number"
              min={min}
              step={1}
              value={amount}
              onChange={(e) => setAmount(Number(e.target.value))}
            />
          </div>
          <div className="field">
            <label>Pay with</label>
            <div className="tabs">
              <button
                type="button"
                className={method === 'card' ? 'active' : ''}
                onClick={() => setMethod('card')}
              >
                Card · instant
              </button>
              <button
                type="button"
                className={method === 'us_bank_account' ? 'active' : ''}
                onClick={() => setMethod('us_bank_account')}
              >
                Bank debit (ACH) · +{b?.bonuses.achBonusPct ?? 3}% bonus
              </button>
              <button
                type="button"
                className={method === 'bank_transfer' ? 'active' : ''}
                onClick={() => setMethod('bank_transfer')}
              >
                Bank transfer / wire · +{b?.bonuses.bankTransferBonusPct ?? 5}% bonus
              </button>
            </div>
          </div>
          {b && b.bonuses.tiers.length > 0 && (
            <p className="muted">
              Volume bonus:{' '}
              {b.bonuses.tiers.map((t) => `${formatMoney(t.minMicroUsd)}+ earns ${t.pct}%`).join(', ')}. Bank
              debits add {b.bonuses.achBonusPct}% and bank transfers {b.bonuses.bankTransferBonusPct}% on top.
            </p>
          )}
          {quote && (
            <div className="example" style={{ marginBottom: 10 }}>
              <div className="stat">You pay {formatMoney(Math.round(amount * 1_000_000))}, you get</div>
              <div className="stat-value">{formatMoney(quote.creditedMicroUsd)}</div>
              {quote.bonusMicroUsd > 0 && (
                <div className="ok">
                  includes {formatMoney(quote.bonusMicroUsd)} bonus ({quote.reasons.join('; ')})
                </div>
              )}
            </div>
          )}
          {method === 'bank_transfer' ? (
            <p className="muted">
              You will get our bank details on the next screen and send the transfer from your bank. Credit
              and bonus appear when the money arrives (same day for wires, 1 to 3 business days for ACH).
              Transfers cannot be used for auto-recharge.
            </p>
          ) : (
            <label>
              <input
                type="checkbox"
                checked={save}
                onChange={(e) => setSave(e.target.checked)}
                style={{ width: 'auto', marginRight: 6 }}
              />
              Save this {method === 'card' ? 'card' : 'bank account'} for auto-recharge
            </label>
          )}
          {error && <p className="error">{error}</p>}
          <button
            disabled={busy}
            style={{ marginTop: 10 }}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                const r = await api<{ checkoutUrl: string }>('/billing/topup', {
                  method: 'POST',
                  json: {
                    amountMicroUsd: Math.round(amount * 1_000_000),
                    paymentMethod: method,
                    savePaymentMethod: save,
                  },
                });
                window.location.assign(r.checkoutUrl);
              } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed');
                setBusy(false);
              }
            }}
          >
            {method === 'card'
              ? 'Continue to payment'
              : method === 'us_bank_account'
                ? 'Continue to bank debit'
                : 'Get transfer details'}
          </button>
        </div>

        <div className="card">
          <h2>Auto-recharge</h2>
          <p className="muted">
            When your available balance drops below the threshold, we charge your saved card or bank account
            for the amount below. Bank debits earn the same bonus and take a few days to clear, so give them a
            higher threshold.
          </p>
          {b && b.savedPaymentMethods.length === 0 && (
            <p className="muted">
              No saved payment method yet. Tick "save for auto-recharge" on your next top-up.
            </p>
          )}
          <div className="field">
            <label>Charge when balance is below (USD)</label>
            <input
              type="number"
              min={0}
              step={1}
              value={arState.threshold}
              onChange={(e) => setAr({ ...arState, threshold: Number(e.target.value) })}
            />
          </div>
          <div className="field">
            <label>Amount to add (USD)</label>
            <input
              type="number"
              min={min}
              step={1}
              value={arState.amount}
              onChange={(e) => setAr({ ...arState, amount: Number(e.target.value) })}
            />
          </div>
          {b && b.savedPaymentMethods.length > 0 && (
            <div className="field">
              <label>Pay with</label>
              <select
                value={arState.methodId}
                onChange={(e) => setAr({ ...arState, methodId: e.target.value })}
              >
                {b.savedPaymentMethods.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.kind === 'card' ? 'Card' : 'Bank account (ACH)'} · {m.label}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="row">
            <button
              className="secondary"
              disabled={!b || b.savedPaymentMethods.length === 0}
              onClick={async () => {
                if (!b) return;
                try {
                  await api('/billing/auto-recharge', {
                    method: 'PUT',
                    json: {
                      enabled: true,
                      thresholdMicroUsd: Math.round(arState.threshold * 1_000_000),
                      amountMicroUsd: Math.round(arState.amount * 1_000_000),
                      paymentMethodId: arState.methodId || undefined,
                    },
                  });
                  setAr(null);
                  await billing.reload();
                } catch (err) {
                  setError(err instanceof Error ? err.message : 'Failed');
                }
              }}
            >
              {b?.autoRecharge.enabled ? 'Save changes' : 'Turn on auto-recharge'}
            </button>
            {b?.autoRecharge.enabled && (
              <button
                className="secondary"
                onClick={async () => {
                  await api('/billing/auto-recharge', { method: 'PUT', json: { enabled: false } });
                  await billing.reload();
                }}
              >
                Turn off
              </button>
            )}
          </div>
          {b?.autoRecharge.enabled && (
            <p className="ok" style={{ marginTop: 10 }}>
              On: below {formatMoney(b.autoRecharge.thresholdMicroUsd)} we add{' '}
              {formatMoney(b.autoRecharge.amountMicroUsd)} using your saved{' '}
              {b.autoRecharge.paymentMethodKind === 'us_bank_account' ? 'bank account' : 'card'}.
            </p>
          )}
        </div>
      </div>

      <div className="card">
        <h2>History</h2>
        <table>
          <thead>
            <tr>
              <th>When</th>
              <th>What</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {b?.ledger.map((l) => (
              <tr key={l.id}>
                <td>{formatDate(l.createdAt)}</td>
                <td>
                  {l.description} <span className="muted">({l.type.replace(/_/g, ' ')})</span>
                </td>
                <td className={l.amountMicroUsd < 0 ? '' : 'ok'}>
                  {formatMoney(l.amountMicroUsd, Math.abs(l.amountMicroUsd) < 10_000)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
